import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pin, PinOff, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../shared/api/client";
import type { PinnedProjectView } from "../../shared/types/api";
import { cn } from "../../shared/ui";

/* One key for the whole feature: the chips, the sidebar and the row buttons all read the same
   list, and three keys would let them drift apart on screen. */
const KEY = ["project-pins"];

export function usePinnedProjects() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<PinnedProjectView[]>("/api/v1/project-pins"),
  });
}

/**
 * Pin, unpin and rearrange.
 *
 * <p>Every call answers with the whole row, which is written straight into the cache rather than
 * triggering a refetch: the server has just told us what the list became, and asking again would
 * be a second round trip to learn the same thing.
 */
export function usePinActions() {
  const cache = useQueryClient();
  const write = (rows: PinnedProjectView[]) => cache.setQueryData(KEY, rows);

  const pin = useMutation({
    mutationFn: (projectId: string) =>
      api.post<PinnedProjectView[]>(`/api/v1/project-pins/${projectId}`, {}),
    onSuccess: write,
  });
  const unpin = useMutation({
    mutationFn: (projectId: string) =>
      api.del<PinnedProjectView[]>(`/api/v1/project-pins/${projectId}`),
    onSuccess: write,
  });
  const reorder = useMutation({
    mutationFn: (projectIds: string[]) =>
      api.put<PinnedProjectView[]>("/api/v1/project-pins/order", { projectIds }),
    onSuccess: write,
  });
  return { pin, unpin, reorder };
}

/** What a dragged row carries. Namespaced so a stray drop elsewhere cannot be mistaken for one. */
const DRAG_TYPE = "application/x-tazlo-project";

export function projectDragProps(projectId: string) {
  return {
    draggable: true,
    onDragStart: (event: React.DragEvent) => {
      event.dataTransfer.setData(DRAG_TYPE, projectId);
      event.dataTransfer.effectAllowed = "copy";
    },
  };
}

/**
 * The row of pinned projects, and the place you drop one to pin it.
 *
 * <p>Dragging is what was asked for, but it is never the only way in: a drag needs a pointer, a
 * steady hand and sight of both ends at once. Every row also carries a pin button, and the chips
 * can be removed with a click.
 *
 * <p>Shown even when empty, because an invisible drop target cannot be discovered. It says what
 * to do instead.
 */
export function PinnedProjectsBar() {
  const pinned = usePinnedProjects();
  const { pin, unpin, reorder } = usePinActions();
  const [over, setOver] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);

  const rows = pinned.data ?? [];

  const dropFromTable = (event: React.DragEvent) => {
    event.preventDefault();
    setOver(false);
    const projectId = event.dataTransfer.getData(DRAG_TYPE);
    if (projectId && !rows.some((row) => row.projectId === projectId)) {
      pin.mutate(projectId);
    }
  };

  /* Rearranging: the dragged chip takes the position of the one it was dropped on, and the rest
     close up behind it. Sent as the whole order — see ProjectPinService.reorder. */
  const dropOnChip = (targetId: string) => {
    if (!dragging || dragging === targetId) {
      return;
    }
    const order = rows.map((row) => row.projectId).filter((id) => id !== dragging);
    order.splice(order.indexOf(targetId), 0, dragging);
    reorder.mutate(order);
    setDragging(null);
  };

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={dropFromTable}
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-xl border border-dashed px-3 py-2 transition-colors",
        over ? "border-brand-400 bg-brand-50/70" : "border-ink-200 bg-ink-50/40",
      )}
    >
      <span className="flex items-center gap-1.5 text-xs font-medium text-ink-500">
        <Pin className="h-3.5 w-3.5" />
        Pinned
      </span>

      {rows.map((row) => (
        <span
          key={row.projectId}
          draggable
          onDragStart={() => setDragging(row.projectId)}
          onDragEnd={() => setDragging(null)}
          onDragOver={(event) => event.preventDefault()}
          /*
           * Only a chip dropped on a chip is a rearrangement. A row dragged in from the table is
           * left to bubble up to the bar, which pins it — swallowing it here is what made
           * dragging a project in stop working as soon as the chips covered the bar, since the
           * drop then landed on a chip rather than on the empty space beside them.
           */
          onDrop={(event) => {
            if (!dragging) {
              return;
            }
            event.stopPropagation();
            dropOnChip(row.projectId);
          }}
          className={cn(
            "group flex items-center gap-1.5 rounded-lg border bg-white px-2 py-1 text-sm shadow-sm",
            dragging === row.projectId
              ? "border-brand-400 opacity-50"
              : "border-ink-200 hover:border-ink-300",
          )}
        >
          <Link
            to={`/projects/${row.projectId}`}
            className="font-medium text-ink-800 hover:text-brand-700"
          >
            <span className="font-mono text-xs text-ink-500">{row.projectKey}</span>{" "}
            {row.name}
          </Link>
          <button
            type="button"
            aria-label={`Unpin ${row.name}`}
            title={`Unpin ${row.name}`}
            onClick={() => unpin.mutate(row.projectId)}
            className="rounded p-0.5 text-ink-300 hover:bg-ink-100 hover:text-ink-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}

      {rows.length === 0 && (
        <span className="text-sm text-ink-500">
          Drag a project here — or use the pin on its row — to keep it within reach.
        </span>
      )}
    </div>
  );
}

/** The pin button that sits on each row of the projects table. */
export function PinButton({ projectId, name }: { projectId: string; name: string }) {
  const pinned = usePinnedProjects();
  const { pin, unpin } = usePinActions();
  const isPinned = (pinned.data ?? []).some((row) => row.projectId === projectId);

  return (
    <button
      type="button"
      aria-label={isPinned ? `Unpin ${name}` : `Pin ${name}`}
      title={isPinned ? "Unpin" : "Pin for quick access"}
      /* The row opens the project, so the button must not also do that. */
      onClick={(event) => {
        event.stopPropagation();
        (isPinned ? unpin : pin).mutate(projectId);
      }}
      className={cn(
        "rounded p-1 transition-colors",
        isPinned ? "text-brand-600 hover:bg-brand-50" : "text-ink-300 hover:bg-ink-100 hover:text-ink-600",
      )}
    >
      {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
    </button>
  );
}
